import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge, PriorityBadge, fmtDate } from "@/components/shared";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FunnelSimple, PencilSimple, Trash } from "@phosphor-icons/react";

const STATUS_OPTIONS = ["", "assigned", "acknowledged", "in_progress", "submitted", "blocked", "completed", "reopened", "extension_requested"];
const PRIORITY_OPTIONS = ["", "low", "normal", "high", "urgent", "critical"];

export default function TasksList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(user.role === "member" ? "my" : "all");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [selected, setSelected] = useState([]);
  const search = params.get("search") || "";
  const isAdmin = user.role === "admin";

  // FIFO highlight: for each assignee across the currently-loaded tasks, find their oldest
  // still-open task — that's the one they should pick up next (mirrors the Orders/PO "FIFO #1" badge).
  const DONE_STATUSES = ["completed", "approved", "cancelled"];
  const oldestOpenTaskIdByAssignee = {};
  tasks.filter((t) => !DONE_STATUSES.includes(t.status)).forEach((t) => {
    (t.assignees || []).forEach((a) => {
      const cur = oldestOpenTaskIdByAssignee[a.user_id];
      if (!cur || new Date(t.created_at) < new Date(cur.created_at)) oldestOpenTaskIdByAssignee[a.user_id] = t;
    });
  });
  const fifoNamesFor = (t) => (t.assignees || []).filter((a) => oldestOpenTaskIdByAssignee[a.user_id]?.id === t.id).map((a) => a.name);

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams({ view, parent: "root" });
    if (status) q.set("status", status);
    if (priority) q.set("priority", priority);
    if (search) q.set("search", search);
    api.get(`/tasks?${q.toString()}`).then((r) => { setTasks(r.data); setSelected([]); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(load, [view, status, priority, search]);

  const toggleSel = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAll = () => setSelected((p) => (p.length === tasks.length ? [] : tasks.map((t) => t.id)));
  const deleteOne = async (t) => {
    if (!window.confirm(`Delete task "${t.title}" permanently?`)) return;
    try { await api.delete(`/tasks/${t.id}`); toast.success("Task deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} selected task(s) permanently?`)) return;
    try { await api.post("/tasks/bulk-delete", { ids: selected }); toast.success(`${selected.length} task(s) deleted`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const Sel = ({ value, onChange, options, placeholder }) => (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 rounded-sm text-sm h-9" data-testid={`filter-${placeholder}`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
        {options.map((o) => (
          <SelectItem key={o || "all"} value={o || "all"} className="capitalize">
            {o ? o.replace(/_/g, " ") : `All ${placeholder}`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] animate-fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-1">Work</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50">Tasks</h1>
        </div>
        <Button onClick={() => navigate("/tasks/new")} data-testid="new-task-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1.5">
          <Plus weight="bold" size={16} /> New Task
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex border border-zinc-800 rounded-sm overflow-hidden">
          {[{ k: "my", l: "My Tasks" }, { k: "created", l: "Created by Me" }, ...(user.role !== "member" ? [{ k: "all", l: "All Tasks" }] : [{ k: "all", l: "Involved" }])].map((v) => (
            <button
              key={v.k}
              onClick={() => setView(v.k)}
              data-testid={`view-${v.k}`}
              className={`px-4 py-2 text-sm transition-colors ${view === v.k ? "bg-emerald-600 text-white font-semibold" : "bg-zinc-900 text-zinc-400 hover:text-zinc-100"}`}
            >
              {v.l}
            </button>
          ))}
        </div>
        <FunnelSimple size={16} className="text-zinc-500 ml-2" />
        <Sel value={status} onChange={setStatus} options={STATUS_OPTIONS} placeholder="status" />
        <Sel value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} placeholder="priority" />
        {search && (
          <button onClick={() => setParams({})} className="text-xs text-emerald-400 hover:underline">
            Clear search "{search}"
          </button>
        )}
      </div>

      {isAdmin && selected.length > 0 && (
        <div className="flex items-center justify-between border border-emerald-600/40 bg-emerald-600/10 px-4 py-2 mb-3 rounded-sm" data-testid="bulk-bar">
          <span className="text-sm text-emerald-300 font-semibold">{selected.length} task(s) selected</span>
          <Button size="sm" onClick={bulkDelete} data-testid="bulk-delete-btn" className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs gap-1"><Trash size={14} /> Delete Selected</Button>
        </div>
      )}

      <div className="border border-zinc-800 bg-zinc-900/30 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 text-left text-[10px] uppercase tracking-wider text-zinc-500">
              {isAdmin && (
                <th className="px-3 py-3 w-8">
                  <Checkbox checked={tasks.length > 0 && selected.length === tasks.length} onCheckedChange={toggleAll} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="select-all-tasks" />
                </th>
              )}
              <th className="px-4 py-3 font-bold">Customer / Ref Code</th>
              <th className="px-4 py-3 font-bold">Title</th>
              <th className="px-4 py-3 font-bold">Priority</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold">Assignees</th>
              <th className="px-4 py-3 font-bold">Due</th>
              <th className="px-4 py-3 font-bold">Remaining</th>
              <th className="px-4 py-3 font-bold">Progress</th>
              <th className="px-4 py-3 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-zinc-500 font-mono text-sm">Loading…</td></tr>
            ) : tasks.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-zinc-500">No tasks found.</td></tr>
            ) : (
              tasks.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => navigate(`/tasks/${t.id}`)}
                  data-testid={`task-row-${t.id}`}
                  className="hover:bg-zinc-900 cursor-pointer transition-colors"
                >
                  {isAdmin && (
                    <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggleSel(t.id)} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid={`select-task-${t.id}`} />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="text-xs text-zinc-200 truncate max-w-[140px]">{t.customer_name || "—"}</div>
                    {t.ref_code && <div className="font-mono text-[10px] text-zinc-500 truncate max-w-[140px]">{t.ref_code}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-100 max-w-xs truncate">
                    {t.title}
                    {fifoNamesFor(t).length > 0 && (
                      <span className="ml-1.5 inline-flex items-center text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-400"
                        title={`Oldest open task for ${fifoNamesFor(t).join(", ")} — do this first (FIFO)`} data-testid={`fifo-badge-task-${t.id}`}>
                        FIFO{view !== "my" ? ` · ${fifoNamesFor(t).join(", ")}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge status={t.is_overdue && !["completed","approved","cancelled"].includes(t.status) ? "overdue" : t.status} /></td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">
                    {(t.assignees || []).map((a) => a.name).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{fmtDate(t.due_at)}</td>
                  <td className={`px-4 py-3 text-xs font-mono whitespace-nowrap ${t.is_overdue ? "text-red-400" : "text-zinc-400"}`}>
                    {t.is_overdue ? `+${t.time.delay}` : t.time.remaining || "—"}
                  </td>
                  <td className="px-4 py-3 w-32">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-600" style={{ width: `${t.progress || 0}%` }} />
                      </div>
                      <span className="text-[11px] text-zinc-500 font-mono w-8">{t.progress || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button onClick={() => navigate(`/tasks/${t.id}?edit=1`)} title="Edit task" data-testid={`edit-task-${t.id}`} className="p-1.5 text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800 rounded-sm transition-colors"><PencilSimple size={15} /></button>
                      {isAdmin && <button onClick={() => deleteOne(t)} title="Delete task" data-testid={`delete-task-${t.id}`} className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-zinc-800 rounded-sm transition-colors"><Trash size={15} /></button>}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
